/**
 * Authentication Service
 * Handles all Cognito authentication operations including signup, signin, password management
 */

import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
  ISignUpResult,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { getCognitoUserPoolId, getCognitoClientId } from '../config';
import { isRetryableError, getRetryDelay, sleep } from '../utils/cognitoErrorMapper';
import { validateEmailDomain, DOMAIN_ERROR_MESSAGE } from '../utils/emailDomainValidator';

// Initialize Cognito User Pool
const poolData = {
  UserPoolId: getCognitoUserPoolId(),
  ClientId: getCognitoClientId(),
};

const userPool = new CognitoUserPool(poolData);

/**
 * User attributes interface
 */
export interface UserAttributes {
  [key: string]: string;
}

/**
 * MFA challenge result returned when Cognito requires MFA verification
 */
export interface MfaChallengeResult {
  cognitoUser: CognitoUser;
  challengeName: string;
}

/**
 * Type guard to discriminate between CognitoUserSession and MfaChallengeResult
 */
export const isMfaChallenge = (
  result: CognitoUserSession | MfaChallengeResult | NewPasswordRequiredResult
): result is MfaChallengeResult => {
  return (result as MfaChallengeResult).challengeName !== undefined
    && (result as NewPasswordRequiredResult).newPasswordRequired !== true;
};

/** Result returned when Cognito requires the user to set a new (permanent) password. */
export interface NewPasswordRequiredResult {
  cognitoUser: CognitoUser;
  userAttributes: Record<string, string>;
  requiredAttributes: string[];
  newPasswordRequired: true;
}

export const isNewPasswordRequired = (
  result: CognitoUserSession | MfaChallengeResult | NewPasswordRequiredResult
): result is NewPasswordRequiredResult => {
  return (result as NewPasswordRequiredResult).newPasswordRequired === true;
};

/**
 * Validation error class
 */
export class ValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ValidationError';
  }
}

/**
 * Password validation requirements
 */
const PASSWORD_MIN_LENGTH = 8;
const PASSWORD_REGEX = {
  uppercase: /[A-Z]/,
  lowercase: /[a-z]/,
  digit: /[0-9]/,
  special: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/,
};

/**
 * Email validation regex (RFC 5322 simplified)
 */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Timeout for Cognito API calls (in milliseconds)
 */
const COGNITO_TIMEOUT = 30000; // 30 seconds

/**
 * Maximum number of retry attempts for retryable errors
 */
const MAX_RETRY_ATTEMPTS = 3;

/**
 * Validate password meets requirements
 * @param password - Password to validate
 * @throws ValidationError if password doesn't meet requirements
 */
export const validatePassword = (password: string): void => {
  if (!password || password.length < PASSWORD_MIN_LENGTH) {
    throw new ValidationError(
      `Password must be at least ${PASSWORD_MIN_LENGTH} characters long`
    );
  }

  if (!PASSWORD_REGEX.uppercase.test(password)) {
    throw new ValidationError('Password must contain at least one uppercase letter');
  }

  if (!PASSWORD_REGEX.lowercase.test(password)) {
    throw new ValidationError('Password must contain at least one lowercase letter');
  }

  if (!PASSWORD_REGEX.digit.test(password)) {
    throw new ValidationError('Password must contain at least one number');
  }

  if (!PASSWORD_REGEX.special.test(password)) {
    throw new ValidationError('Password must contain at least one special character');
  }
};

/**
 * Validate email format
 * @param email - Email to validate
 * @throws ValidationError if email format is invalid
 */
export const validateEmail = (email: string): void => {
  if (!email || !EMAIL_REGEX.test(email)) {
    throw new ValidationError('Invalid email format');
  }
};

/**
 * Wrap a promise with a timeout
 * @param promise - Promise to wrap
 * @param timeoutMs - Timeout in milliseconds
 * @returns Promise that rejects if timeout is exceeded
 */
const withTimeout = <T>(promise: Promise<T>, timeoutMs: number): Promise<T> => {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error('Request timed out')), timeoutMs)
    ),
  ]);
};

/**
 * Retry a function with exponential backoff for retryable errors
 * @param fn - Function to retry
 * @param maxAttempts - Maximum number of attempts
 * @returns Promise resolving to function result
 */
const withRetry = async <T>(
  fn: () => Promise<T>,
  maxAttempts: number = MAX_RETRY_ATTEMPTS
): Promise<T> => {
  let lastError: any;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;

      // Check if error is retryable
      if (!isRetryableError(error)) {
        throw error;
      }

      // Don't retry on last attempt
      if (attempt === maxAttempts - 1) {
        throw error;
      }

      // Wait before retrying with exponential backoff
      const delay = getRetryDelay(attempt);
      await sleep(delay);
    }
  }

  throw lastError;
};

/**
 * Sign up a new user
 * @param email - User email
 * @param password - User password
 * @param attributes - Additional user attributes
 * @returns Promise resolving to signup result
 */
export const signUp = async (
  email: string,
  password: string,
  attributes: UserAttributes = {}
): Promise<ISignUpResult> => {
  // Validate email format
  validateEmail(email);

  // Validate email domain (must be @dfx5.com)
  if (!validateEmailDomain(email)) {
    throw new ValidationError(DOMAIN_ERROR_MESSAGE);
  }

  // Validate password requirements
  validatePassword(password);

  return withRetry(() =>
    withTimeout(
      new Promise<ISignUpResult>((resolve, reject) => {
        const attributeList: CognitoUserAttribute[] = [];

        // Add email attribute
        attributeList.push(
          new CognitoUserAttribute({
            Name: 'email',
            Value: email,
          })
        );

        // Add any additional attributes
        Object.keys(attributes).forEach((key) => {
          if (key !== 'email') {
            attributeList.push(
              new CognitoUserAttribute({
                Name: key,
                Value: attributes[key],
              })
            );
          }
        });

        userPool.signUp(email, password, attributeList, [], (err, result) => {
          if (err) {
            reject(err);
            return;
          }
          resolve(result!);
        });
      }),
      COGNITO_TIMEOUT
    )
  );
};

/**
 * Confirm user signup with verification code
 * @param email - User email
 * @param code - Verification code from email
 * @returns Promise resolving when confirmation succeeds
 */
export const confirmSignUp = async (email: string, code: string): Promise<void> => {
  return withRetry(() =>
    withTimeout(
      new Promise<void>((resolve, reject) => {
        const userData = {
          Username: email,
          Pool: userPool,
        };

        const cognitoUser = new CognitoUser(userData);

        cognitoUser.confirmRegistration(code, true, (err) => {
          if (err) {
            reject(err);
            return;
          }
          resolve();
        });
      }),
      COGNITO_TIMEOUT
    )
  );
};

/**
 * Sign in a user
 * @param email - User email
 * @param password - User password
 * @returns Promise resolving to user session with JWT tokens
 */
export const signIn = async (
  email: string,
  password: string
): Promise<CognitoUserSession | MfaChallengeResult | NewPasswordRequiredResult> => {
  return withRetry(() =>
    withTimeout(
      new Promise<CognitoUserSession | MfaChallengeResult | NewPasswordRequiredResult>((resolve, reject) => {
        const authenticationData = {
          Username: email,
          Password: password,
        };

        const authenticationDetails = new AuthenticationDetails(authenticationData);

        const userData = {
          Username: email,
          Pool: userPool,
        };

        const cognitoUser = new CognitoUser(userData);

        cognitoUser.authenticateUser(authenticationDetails, {
          onSuccess: (session) => {
            resolve(session);
          },
          onFailure: (err) => {
            reject(err);
          },
          mfaRequired: (challengeName) => {
            resolve({ cognitoUser, challengeName });
          },
          newPasswordRequired: (userAttributes, requiredAttributes) => {
            // Cognito includes non-writable attrs (e.g. email_verified) that must not be
            // resubmitted; the SignInPage passes required attrs through completeNewPassword.
            resolve({
              cognitoUser,
              userAttributes: userAttributes as Record<string, string>,
              requiredAttributes: (requiredAttributes ?? []) as string[],
              newPasswordRequired: true,
            });
          },
        });
      }),
      COGNITO_TIMEOUT
    )
  );
};

/**
 * Submit MFA verification code to complete sign-in
 * @param cognitoUser - CognitoUser instance from the MFA challenge
 * @param code - The 6-digit verification code sent to the user's email
 * @param mfaType - MFA type, defaults to 'EMAIL_OTP'
 * @returns Promise resolving to authenticated user session
 */
export const submitMfaCode = async (
  cognitoUser: CognitoUser,
  code: string,
  mfaType: string = 'EMAIL_OTP'
): Promise<CognitoUserSession> => {
  return withRetry(() =>
    withTimeout(
      new Promise<CognitoUserSession>((resolve, reject) => {
        cognitoUser.sendMFACode(
          code,
          {
            onSuccess: (session) => {
              resolve(session);
            },
            onFailure: (err) => {
              reject(err);
            },
          },
          mfaType
        );
      }),
      COGNITO_TIMEOUT
    )
  );
};

/** Complete the FORCE_CHANGE_PASSWORD challenge with a new permanent password. */
export const completeNewPassword = async (
  cognitoUser: CognitoUser,
  newPassword: string,
  requiredAttributes: Record<string, string> = {}
): Promise<CognitoUserSession> => {
  return withTimeout(
    new Promise<CognitoUserSession>((resolve, reject) => {
      cognitoUser.completeNewPasswordChallenge(newPassword, requiredAttributes, {
        onSuccess: (session) => resolve(session),
        onFailure: (err) => reject(err),
      });
    }),
    COGNITO_TIMEOUT
  );
};

/**
 * Sign out the current user
 * @returns Promise resolving when signout completes
 */
export const signOut = async (): Promise<void> => {
  return new Promise((resolve) => {
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) {
      cognitoUser.signOut();
    }
    resolve();
  });
};

/**
 * Request password reset code
 * @param email - User email
 * @returns Promise resolving when reset code is sent
 */
export const forgotPassword = async (email: string): Promise<void> => {
  return withRetry(() =>
    withTimeout(
      new Promise<void>((resolve, reject) => {
        const userData = {
          Username: email,
          Pool: userPool,
        };

        const cognitoUser = new CognitoUser(userData);

        cognitoUser.forgotPassword({
          onSuccess: () => {
            resolve();
          },
          onFailure: (err) => {
            reject(err);
          },
        });
      }),
      COGNITO_TIMEOUT
    )
  );
};

/**
 * Confirm password reset with verification code
 * @param email - User email
 * @param code - Verification code from email
 * @param newPassword - New password
 * @returns Promise resolving when password is reset
 */
export const confirmPassword = async (
  email: string,
  code: string,
  newPassword: string
): Promise<void> => {
  // Validate new password
  validatePassword(newPassword);

  return withRetry(() =>
    withTimeout(
      new Promise<void>((resolve, reject) => {
        const userData = {
          Username: email,
          Pool: userPool,
        };

        const cognitoUser = new CognitoUser(userData);

        cognitoUser.confirmPassword(code, newPassword, {
          onSuccess: () => {
            resolve();
          },
          onFailure: (err) => {
            reject(err);
          },
        });
      }),
      COGNITO_TIMEOUT
    )
  );
};

/**
 * Change password for authenticated user
 * @param oldPassword - Current password
 * @param newPassword - New password
 * @returns Promise resolving when password is changed
 */
export const changePassword = async (
  oldPassword: string,
  newPassword: string
): Promise<void> => {
  // Validate new password
  validatePassword(newPassword);

  return withRetry(() =>
    withTimeout(
      new Promise<void>((resolve, reject) => {
        const cognitoUser = userPool.getCurrentUser();

        if (!cognitoUser) {
          reject(new Error('No authenticated user'));
          return;
        }

        cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
          if (err || !session) {
            reject(err || new Error('Failed to get session'));
            return;
          }

          cognitoUser.changePassword(oldPassword, newPassword, (err) => {
            if (err) {
              reject(err);
              return;
            }
            resolve();
          });
        });
      }),
      COGNITO_TIMEOUT
    )
  );
};

/**
 * Resend confirmation code
 * @param email - User email
 * @returns Promise resolving when code is resent
 */
export const resendConfirmationCode = async (email: string): Promise<void> => {
  return withRetry(() =>
    withTimeout(
      new Promise<void>((resolve, reject) => {
        const userData = {
          Username: email,
          Pool: userPool,
        };

        const cognitoUser = new CognitoUser(userData);

        cognitoUser.resendConfirmationCode((err) => {
          if (err) {
            reject(err);
            return;
          }
          resolve();
        });
      }),
      COGNITO_TIMEOUT
    )
  );
};

/**
 * Get current authenticated user
 * @returns Promise resolving to CognitoUser or null if not authenticated
 */
export const getCurrentUser = async (): Promise<CognitoUser | null> => {
  return new Promise((resolve, reject) => {
    const cognitoUser = userPool.getCurrentUser();

    if (!cognitoUser) {
      resolve(null);
      return;
    }

    cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err) {
        reject(err);
        return;
      }

      if (!session || !session.isValid()) {
        resolve(null);
        return;
      }

      resolve(cognitoUser);
    });
  });
};

/**
 * Get current user session
 * @returns Promise resolving to CognitoUserSession or null if not authenticated
 */
export const getCurrentSession = async (): Promise<CognitoUserSession | null> => {
  return new Promise((resolve, reject) => {
    const cognitoUser = userPool.getCurrentUser();

    if (!cognitoUser) {
      resolve(null);
      return;
    }

    cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err) {
        reject(err);
        return;
      }

      if (!session || !session.isValid()) {
        resolve(null);
        return;
      }

      resolve(session);
    });
  });
};

/**
 * Refresh the current session
 * @returns Promise resolving to new CognitoUserSession
 */
export const refreshSession = async (): Promise<CognitoUserSession> => {
  return withRetry(() =>
    withTimeout(
      new Promise<CognitoUserSession>((resolve, reject) => {
        const cognitoUser = userPool.getCurrentUser();

        if (!cognitoUser) {
          reject(new Error('No authenticated user'));
          return;
        }

        cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
          if (err || !session) {
            reject(err || new Error('Failed to get session'));
            return;
          }

          const refreshToken = session.getRefreshToken();

          cognitoUser.refreshSession(refreshToken, (err, newSession) => {
            if (err) {
              reject(err);
              return;
            }
            resolve(newSession);
          });
        });
      }),
      COGNITO_TIMEOUT
    )
  );
};
